import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

// Rxjs import
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// 3rd party imports
import { classToPlain, plainToClass } from 'class-transformer';

// services imports
import { MPCacheRegistrationService } from '../../../../_services/shared-services/mp-cache-registration-service';

// Constants imports
import { CacheDuration } from '../../../../_constants/cache-duration';
import { URLConstants } from '../../../../_constants/url-constants';

// models imports
import { APAdjustmentParameterDto } from '../../_models/accounts-payable/ap-adjustment/ap-adjustment-parameter-dto';
import { APAdjustmentResultDto } from '../../_models/accounts-payable/ap-adjustment/ap-adjustment-result-dto';
import { TransactionLogResultDto } from '../../_models/common/transaction-log-result-dto';
import { APAdjustmentEditorResultDto } from '../../_models/accounts-payable/ap-adjustment/ap-adjustment-editor-result-dto';
import { SubCategoryByGlAccountParametersDto } from '../../_models/common/subcategory-by-gl-account-parametere-dto';
import { GLSubcategorySelectorResultDto } from '../../_models/common/gl-account/glsubcategory-selector-result-dto';
import { APAdjustmentRawDto } from '../../_models/accounts-payable/ap-adjustment/ap-adjustment-raw-dto';
import { TransactionLogParameterDto } from '../../_models/common/transaction-log-parameter-dto';
import { APAdjustmentReverseParameterDto } from '../../_models/accounts-payable/ap-adjustment/ap-adjustment-reverse-parameter-dto';
import { APAdjustmentEditorForApplyPaymentWithAPCMParameterDto } from '../../_models/accounts-payable/ap-adjustment/ap-adjustment-editor-for-apply-payment-with-apcm-parameter-dto';
import { ApplyPaymentWithAPCMParameterDto } from '../../_models/accounts-payable/ap-adjustment/apply-payment-with-apcm-parameter-dto';
import { APAdjustmentEditorForApplyPaymentWithAPCMResultDto } from '../../_models/accounts-payable/ap-adjustment/ap-adjustment-editor-for-apply-payment-with-apcm-result-dto';
import { ReverseEditorResultDto } from '../../../../../app/shared-module/_models/reverse-editor-result-dto';

@Injectable({
    providedIn: 'root'
})
export class APAdjustmentService {
    constructor(
        private readonly httpClient: HttpClient,
        private readonly cacheRegistrationService: MPCacheRegistrationService
    ) {}

    getAPAdjustmentDetail(param: APAdjustmentParameterDto): Observable<APAdjustmentResultDto> {
        const url = `${URLConstants.ccCBAPAdjustmentApiUrl}/detail${param.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, param.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get<APAdjustmentResultDto>(url, options)
            .pipe(map((response: APAdjustmentResultDto) => plainToClass(APAdjustmentResultDto, response)));
    }

    getAPAdjustmentHistory(baseParam: TransactionLogParameterDto): Observable<TransactionLogResultDto[]> {
        const url = `${URLConstants.ccCBAPAdjustmentApiUrl}/history${baseParam.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParam.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get<TransactionLogResultDto[]>(url, options)
            .pipe(map((response: TransactionLogResultDto[]) => plainToClass(TransactionLogResultDto, response)));
    }

    getAPAdjustmentEditor(param: APAdjustmentParameterDto): Observable<APAdjustmentEditorResultDto> {
        const url = `${URLConstants.ccCBAPAdjustmentApiUrl}/editor${param.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, param.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get<APAdjustmentEditorResultDto>(url, options)
            .pipe(map((response: APAdjustmentEditorResultDto) => plainToClass(APAdjustmentEditorResultDto, response)));
    }

    getEditorForApplyPaymentWithAPCM(
        param: APAdjustmentEditorForApplyPaymentWithAPCMParameterDto
    ): Observable<APAdjustmentEditorForApplyPaymentWithAPCMResultDto> {
        const url = `${URLConstants.ccCBAPAdjustmentApiUrl}/editor/apply-payment-with-apcm${param.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(
                map((response: APAdjustmentEditorForApplyPaymentWithAPCMResultDto) =>
                    plainToClass(APAdjustmentEditorForApplyPaymentWithAPCMResultDto, response)
                )
            );
    }

    getSubCategories(baseParam: SubCategoryByGlAccountParametersDto): Observable<GLSubcategorySelectorResultDto[]> {
        const url = `${URLConstants.ccCBAPAdjustmentApiUrl}/subcategories${baseParam.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParam.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get<GLSubcategorySelectorResultDto[]>(url, options)
            .pipe(
                map((response: GLSubcategorySelectorResultDto[]) =>
                    plainToClass(GLSubcategorySelectorResultDto, response)
                )
            );
    }

    addNewAPAdjustment(baseParam: APAdjustmentRawDto): Observable<any> {
        return this.httpClient
            .post<string>(`${URLConstants.ccCBAPAdjustmentApiUrl}`, classToPlain(baseParam))
            .pipe(map((responseData: any) => responseData));
    }

    applyPaymentWithAPCM(baseParam: ApplyPaymentWithAPCMParameterDto): Observable<any> {
        const url = `${URLConstants.ccCBAPAdjustmentApiUrl}/apply-payment-with-apcm`;
        return this.httpClient
            .post<string>(url, classToPlain(baseParam))
            .pipe(map((responseData: any) => responseData));
    }

    editAPAdjustment(baseParam: APAdjustmentRawDto): Observable<any> {
        return this.httpClient
            .put<string>(`${URLConstants.ccCBAPAdjustmentApiUrl}`, classToPlain(baseParam))
            .pipe(map((responseData: any) => responseData));
    }

    deleteAPAdjustment(baseParam: APAdjustmentParameterDto): Observable<any> {
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            }),
            body: classToPlain(baseParam)
        };
        return this.httpClient.delete(URLConstants.ccCBAPAdjustmentApiUrl, options);
    }

    reverseAPAdjustment(baseParam: APAdjustmentReverseParameterDto): Observable<any> {
        return this.httpClient
            .put<string>(`${URLConstants.ccCBAPAdjustmentApiUrl}/reverse`, classToPlain(baseParam))
            .pipe(map((responseData: any) => responseData));
    }

    getAdjustmentReverseEditor(baseParam: APAdjustmentParameterDto): Observable<ReverseEditorResultDto> {
        const url = `${URLConstants.ccCBAPAdjustmentApiUrl}/reverse/editor${baseParam.getQueryString()}`;

        return this.httpClient
            .get<ReverseEditorResultDto>(url)
            .pipe(map((response: ReverseEditorResultDto) => plainToClass(ReverseEditorResultDto, response)));
    }
}
